import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import DashboardStats from "./components/DashboardStats";
import OrderCard from "./components/OrderCard";
import { mockOrders } from "@/mocks/orders";
import SEO from "@/components/base/SEO";

type StatusFilter = "all" | "pending" | "processing" | "shipped" | "delivered";

export default function DashboardPage() {
  const [orders, setOrders] = useState(mockOrders);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const handleUpdateTracking = (orderId: string, tracking: string, carrier: string) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? { ...o, trackingNumber: tracking, carrier, status: "shipped" } : o
      )
    );
  };

  const filtered = orders.filter((o) => {
    const matchStatus = statusFilter === "all" || o.status === statusFilter;
    const matchSearch = !searchQuery || o.buyer.toLowerCase().includes(searchQuery.toLowerCase()) || o.id.toLowerCase().includes(searchQuery.toLowerCase()) || o.productTitle.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  const statusTabs: { value: StatusFilter; label: string; count: number }[] = [
    { value: "all", label: "All Orders", count: orders.length },
    { value: "pending", label: "Pending", count: orders.filter((o) => o.status === "pending").length },
    { value: "processing", label: "Processing", count: orders.filter((o) => o.status === "processing").length },
    { value: "shipped", label: "Shipped", count: orders.filter((o) => o.status === "shipped").length },
    { value: "delivered", label: "Delivered", count: orders.filter((o) => o.status === "delivered").length },
  ];

  return (
    <div className="min-h-screen" style={{backgroundColor: "#f5f5f5"}}>
      <SEO title="Seller Dashboard — StarBookPrime" description="Manage your orders and products." canonical="/dashboard" noIndex={true} />
      <Navbar />
      <div className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900">Seller Dashboard</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your orders and send tracking numbers to customers</p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/upload"
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" /> Add Product
              </Link>
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-download-line" /> Export
              </button>
            </div>
          </div>

          {/* Stats */}
          <DashboardStats />

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by order ID, buyer, or product..."
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              {/* Status Tabs */}
              <div className="flex flex-wrap gap-1.5">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setStatusFilter(tab.value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${statusFilter === tab.value ? "bg-orange-500 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                  >
                    {tab.label}
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${statusFilter === tab.value ? "bg-white/20 text-white" : "bg-gray-200 text-gray-500"}`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Orders List */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-50 rounded-full">
                <i className="ri-inbox-line text-3xl text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No orders found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((order) => (
                <OrderCard key={order.id} order={order} onUpdateTracking={handleUpdateTracking} />
              ))}
            </div>
          )}

          {/* Influencer Referral Summary */}
          <div className="mt-10 bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 rounded-2xl p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-star-fill text-rose-500" /> Influencer Referral Summary
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { name: "Jessica Martinez", platform: "tiktok", icon: "ri-tiktok-line", orders: 2, commission: "$12.00", color: "text-gray-900" },
                { name: "DeShawn Williams", platform: "tiktok", icon: "ri-tiktok-line", orders: 1, commission: "$10.50", color: "text-gray-900" },
                { name: "Tyler Brooks", platform: "twitter", icon: "ri-twitter-x-line", orders: 1, commission: "$8.50", color: "text-gray-900" },
              ].map((inf) => (
                <div key={inf.name} className="bg-white rounded-xl p-4 border border-rose-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-rose-50 rounded-full">
                      <i className={`${inf.icon} text-rose-500`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{inf.name}</p>
                      <p className="text-xs text-gray-400 capitalize">{inf.platform}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{inf.orders} order{inf.orders > 1 ? "s" : ""}</span>
                    <span className="font-bold text-green-600">{inf.commission} owed</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
